import { useState } from 'react';
import supabase from '../lib/supabase';

// Default logo SVG as a data URL - Circular Blue Emblem with AOX
export const DEFAULT_LOGO = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><circle cx="100" cy="100" r="100" fill="#1d4ed8"/><path d="M100 20 L100 180" stroke="white" stroke-width="2" opacity="0.1"/><path d="M20 100 L180 100" stroke="white" stroke-width="2" opacity="0.1"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-weight="bold" font-size="50" fill="white" text-anchor="middle" dy=".3em">ΑΟΧ</text><text x="50%" y="80%" font-family="Arial, sans-serif" font-size="14" fill="white" text-anchor="middle" opacity="0.8">1945</text></svg>'
)}`;

/**
 * Hook for handling file uploads to Supabase Storage
 * @returns {Object} Upload methods and state
 */
export const useStorage = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  /**
   * Upload a file to Supabase Storage
   * @param {File} file - The file to upload
   * @param {string} bucket - The bucket to upload to
   * @param {string} path - The path within the bucket
   * @returns {Promise<string|null>} The public URL of the uploaded file or null on error
   */
  const uploadFile = async (file, bucket = 'team_logos', path = '') => {
    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload an image file.');
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size too large. Please upload a file smaller than 5MB.');
      }

      // Generate a unique file name to prevent conflicts
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 15);
      const fileName = `${path}${timestamp}_${randomStr}.${fileExt}`;

      console.log('Uploading file:', fileName, 'to bucket:', bucket);

      // Upload the file
      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false // Don't overwrite existing files
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('Upload successful:', data);

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      console.log('Public URL:', publicUrl);
      setProgress(100);
      return publicUrl;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  /**
   * Get a public URL for a file in Supabase Storage
   * @param {string} path - The path to the file
   * @param {string} bucket - The bucket containing the file
   * @returns {string} The public URL of the file
   */
  const getPublicUrl = (path, bucket = 'team_logos') => {
    if (!path) return DEFAULT_LOGO;
    
    // If the path is already a full URL, return it
    if (path.startsWith('http')) return path;

    try {
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);
      return publicUrl;
    } catch (err) {
      console.error('Error getting public URL:', err);
      return DEFAULT_LOGO;
    }
  };

  return { uploadFile, getPublicUrl, uploading, error, progress, DEFAULT_LOGO };
};